import {
  Kanban,
  MessageSquare,
  MoreHorizontal,
  Table2,
  Target,
  TrendingUp,
  Workflow,
  type LucideIcon
} from 'lucide-react'

import {
  CompanySizeEnum,
  PrimaryJobEnum,
  AttributionSourceEnum,
  type CompanySizeEnum as CompanySize,
  type PrimaryJobEnum as PriorityKey,
  type AttributionSourceEnum as AttributionKey
} from 'src/api/main'

import type { PreviewTargetKey, SpotlightKey, StepKey } from './types'

/** The steps of the flow, in order. `done` is reached after the last one. */
export const STEP_ORDER = [
  'details',
  'template',
  'attribution',
  'invite'
] as const satisfies readonly StepKey[]

/** A step the wizard can sit on — i.e. everything except `done`. */
export type WizardStepKey = (typeof STEP_ORDER)[number]

/** Position of a step in the flow, or -1 for `done`. */
export const stepIndexOf = (step: StepKey): number =>
  (STEP_ORDER as readonly StepKey[]).indexOf(step)

/**
 * The preset that seeds nothing. Filtered out of the tile grid, where it is
 * offered as the "clean workspace" alternative rather than as one more industry
 * — but it is the value that travels as the profile's `industry`, since the
 * platform requires one and this is the codename for "seed me nothing".
 */
export const NO_DATA_PRESET_CODENAME = 'no_data'

export const COMPANY_SIZES: CompanySize[] = [
  CompanySizeEnum['1_10'],
  CompanySizeEnum['11_50'],
  CompanySizeEnum['51_200'],
  CompanySizeEnum['201_1000']
]

/**
 * The jobs people actually hire a CRM for, ordered by how often they are the
 * reason someone signs up: pipeline and lost deals first, reporting and the
 * escape-from-spreadsheets last. `other` closes the list and spans the row.
 */
/**
 * One icon per priority. Unlike the preset tiles these have no backend
 * descriptor to borrow from, so the mapping lives here — shared by the step
 * that asks and by the summary panel that reports the answer back.
 */
export const PRIORITY_ICONS: Record<PriorityKey, LucideIcon> = {
  deal_pipeline: Kanban,
  stop_losing_deals: Target,
  automate_routine: Workflow,
  forecast_revenue: TrendingUp,
  customer_support: MessageSquare,
  off_spreadsheets: Table2,
  other: MoreHorizontal
}

export const PRIORITIES: PriorityKey[] = [
  PrimaryJobEnum.DEAL_PIPELINE,
  PrimaryJobEnum.STOP_LOSING_DEALS,
  PrimaryJobEnum.AUTOMATE_ROUTINE,
  PrimaryJobEnum.FORECAST_REVENUE,
  PrimaryJobEnum.CUSTOMER_SUPPORT,
  PrimaryJobEnum.OFF_SPREADSHEETS,
  PrimaryJobEnum.OTHER
]

/** Discovery channels a B2B CRM actually gets found through. */
export const ATTRIBUTIONS: AttributionKey[] = [
  AttributionSourceEnum.GOOGLE,
  AttributionSourceEnum.YOUTUBE,
  AttributionSourceEnum.LINKEDIN,
  AttributionSourceEnum.REVIEWS,
  AttributionSourceEnum.REFERRAL,
  AttributionSourceEnum.AI_ASSISTANT,
  AttributionSourceEnum.EVENT,
  AttributionSourceEnum.PRESS,
  AttributionSourceEnum.OTHER
]

/**
 * Sign-in methods a new workspace starts with. Onboarding no longer asks about
 * them — everything here is editable afterwards in Authentication, which is the
 * only place that can also collect OIDC credentials. Password plus the email
 * one-time link is the smallest pair that cannot lock the owner out.
 */
export const DEFAULT_AUTH_METHODS = {
  password_enabled: true,
  email_otp_enabled: true,
  qr_login_enabled: false,
  passkey_enabled: false,
  google_enabled: false,
  entraid_enabled: false,
  apple_enabled: false
} as const

/** How many of {@link DEFAULT_AUTH_METHODS} are on — shown in the preview. */
export const DEFAULT_AUTH_METHOD_COUNT =
  Object.values(DEFAULT_AUTH_METHODS).filter(Boolean).length

/** Where the ring goes for a focused field, and what the caption says. */
export const SPOTLIGHT: Record<SpotlightKey, PreviewTargetKey> = {
  name: 'hero',
  logo: 'sidebarTile',
  handle: 'urlPill',
  domain: 'domainMeta',
  template: 'appsStat',
  members: 'membersStat',
  role: 'roleMeta'
}

/**
 * Unscaled width the preview mock is authored at, and the width the panel is
 * sized to show 1:1. Kept close to the pane's real width on purpose — the
 * further apart the two are, the harder the scaled-down text is to read.
 */
export const PREVIEW_MOCK_WIDTH = 850

/** Stage height used until the panel has measured itself. */
export const PREVIEW_MOCK_HEIGHT = 730

/**
 * DOM id marking a spotlight target inside the preview. Ids rather than refs:
 * the mock is built from AntD components, which forward `id` to their root
 * element but do not all forward `ref`.
 */
export const spotId = (key: PreviewTargetKey): string =>
  `onboarding-spot-${key}`

/**
 * How long the name has to settle before the workspace tile takes its colour
 * from it. The colour is hashed from the name, so recolouring per keystroke
 * cycles the whole palette while someone types a word.
 */
export const AVATAR_SETTLE_MS = 300

export const HANDLE_MIN_LENGTH = 3
export const HANDLE_MAX_LENGTH = 28

/**
 * Cyrillic → Latin, so a Russian workspace name still produces a usable
 * address. Without it `slugifyHandle` strips the whole name and the user hits
 * "at least 3 characters" on a field they never touched.
 */
const CYRILLIC_TO_LATIN: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'i',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya'
}

const transliterate = (value: string): string =>
  value.replace(/[а-яё]/g, (char) => CYRILLIC_TO_LATIN[char] ?? '')

/**
 * What the address field accepts *while it is being typed*.
 *
 * Deliberately looser than {@link slugifyHandle}: it keeps a trailing dash, so
 * `my-` can exist long enough for the next character to be typed. Slugifying on
 * every keystroke eats the dash the moment it appears, which makes a dash
 * impossible to enter at all. The value is canonicalised on blur instead.
 */
export const sanitizeHandleInput = (value: string): string =>
  transliterate(value.toLowerCase())
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, HANDLE_MAX_LENGTH)

/**
 * Slug used for the workspace address. Mirrors the backend's `domain_name`
 * constraints: lowercase alphanumerics and dashes, must start with a letter.
 */
export const slugifyHandle = (value: string): string => {
  const normalized = transliterate(value.toLowerCase())
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, HANDLE_MAX_LENGTH)

  if (!normalized) return ''

  return /^[a-z]/.test(normalized) ? normalized : `w-${normalized}`
}
